import React from 'react';
import Input from '../../../components/ui/Input';
import { cn } from '../../../utils/cn';

const PersonalInformationSection = ({
  formData,
  errors,
  onFieldChange,
  originalUser
}) => {
  // Check if field has changes
  const hasNameChanged = formData?.name !== originalUser?.name;
  const hasEmailChanged = formData?.email !== originalUser?.email;

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Personal Information
        </h2>
        <p className="text-sm text-muted-foreground">
          Update user's basic profile information
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
            Full Name <span className="text-red-500">*</span>
            {hasNameChanged && (
              <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                Modified
              </span>
            )}
          </label>
          <Input
            id="name"
            type="text"
            placeholder="Enter full name"
            value={formData?.name || ''}
            onChange={(e) => onFieldChange?.('name', e?.target?.value)}
            className={cn(
              errors?.name && 'border-red-300 focus:border-red-500 focus:ring-red-200',
              hasNameChanged && 'border-orange-300 focus:border-orange-500 focus:ring-orange-200'
            )}
            autoComplete="name"
          />
          {errors?.name && (
            <p className="mt-1 text-xs text-red-600">
              {errors?.name}
            </p>
          )}
          {hasNameChanged && !errors?.name && (
            <p className="mt-1 text-xs text-orange-600">
              Name will be changed from "{originalUser?.name}" to "{formData?.name}"
            </p>
          )}
        </div>

        {/* Email Address */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email Address <span className="text-red-500">*</span>
            {hasEmailChanged && (
              <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                Modified
              </span>
            )}
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Enter email address"
            value={formData?.email || ''}
            onChange={(e) => onFieldChange?.('email', e?.target?.value)}
            className={cn(
              errors?.email && 'border-red-300 focus:border-red-500 focus:ring-red-200',
              hasEmailChanged && 'border-orange-300 focus:border-orange-500 focus:ring-orange-200'
            )}
            autoComplete="email"
          />
          {errors?.email && (
            <p className="mt-1 text-xs text-red-600">
              {errors?.email}
            </p>
          )}
          {hasEmailChanged && !errors?.email && (
            <p className="mt-1 text-xs text-orange-600">
              Email will be changed from "{originalUser?.email}" to "{formData?.email}"
              <br />
              <span className="text-xs text-muted-foreground">
                User will need to verify the new email address
              </span>
            </p>
          )}
        </div>
      </div>
      {/* Change Impact Information */}
      {(hasNameChanged || hasEmailChanged) && (
        <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h4 className="font-medium text-orange-800 mb-1">Profile Change Impact</h4>
              <ul className="text-sm text-orange-700 space-y-1">
                {hasNameChanged && (
                  <li>• User's display name will be updated throughout the system</li>
                )}
                {hasEmailChanged && (
                  <li>• User will receive an email verification notice at the new address</li>
                )}
                <li>• User will be notified of these profile changes</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Email Uniqueness Info */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Email Validation</p>
            <p className="text-xs">
              We'll automatically check if this email address is already in use by another user in the system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformationSection;